import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, QrCode, X, Mail, Lock, LogIn, Send, CheckCircle, AlertCircle, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUserStore } from '@/store/userStore';
import { useThemeStore } from '@/store/themeStore';
import { apiLogin, apiAccessRequest } from '@/services/api';
import SurveyModal from '@/components/surveys/SurveyModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_NEGATIVO = "https://customer-assets.emergentagent.com/job_926fe77a-4e51-48c9-adda-b35f91e2b951/artifacts/8zkzzvf3_VALORIZA_color_negativo%20%281%29.svg";
const LOGO_POSITIVO = "/valoriza_positive.png";

const DEMO_ROLES = [
  { role: 'superadmin', label: 'Super Admin', desc: 'Acceso total' },
  { role: 'gerente', label: 'Gerente', desc: 'Acceso a sus datos' },
  { role: 'viewer', label: 'Viewer', desc: 'Solo lectura' },
];

const SURVEYS = [
  {
    type: 'ops',
    label: 'OPS',
    desc: 'Observación de Seguridad',
    url: 'https://survey123.arcgis.app/?itemID=15d2de6e6e9b4e0b83bd5a2468a2e626',
    color: '#4A9EFF',
    qrUrl: 'https://customer-assets.emergentagent.com/job_926fe77a-4e51-48c9-adda-b35f91e2b951/artifacts/fyctjhdj_image.png',
  },
  {
    type: 'liderazgo',
    label: 'Liderazgo',
    desc: 'Actividad de Liderazgo',
    url: 'https://survey123.arcgis.app/?itemID=f2785f80942647c28abd34124e112f89',
    color: '#00C853',
    qrUrl: 'https://customer-assets.emergentagent.com/job_926fe77a-4e51-48c9-adda-b35f91e2b951/artifacts/dqqytxdo_image.png',
  },
  {
    type: 'near_misses',
    label: 'Near Miss',
    desc: 'Cuasi-Accidente',
    url: 'https://survey123.arcgis.app/?itemID=ca4f4d12e1ce476f8b7423acd867ecad',
    color: '#FF6B35',
    qrUrl: 'https://customer-assets.emergentagent.com/job_926fe77a-4e51-48c9-adda-b35f91e2b951/artifacts/5e3vjlgf_image.png',
  },
];

function QRCard({ survey, onOpen }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      whileHover={{ scale: 1.03, y: -2 }}
      transition={{ duration: 0.2 }}
      className="flex flex-col items-center gap-2 cursor-pointer"
      onClick={() => onOpen(survey)}
      data-testid={`qr-card-${survey.type}`}
    >
      <div
        className="relative rounded-2xl overflow-hidden p-2.5 transition-all duration-200"
        style={{
          background: `${survey.color}15`,
          border: `1px solid ${survey.color}${hovered ? '60' : '25'}`,
          boxShadow: hovered ? `0 8px 24px ${survey.color}25` : 'none',
        }}
      >
        <div className="relative w-20 h-20 rounded-xl overflow-hidden">
          <img
            src={survey.qrUrl}
            alt={`QR ${survey.label}`}
            className="w-full h-full object-cover"
          />
          {/* Hover overlay */}
          <AnimatePresence>
            {hovered && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex items-center justify-center rounded-xl"
                style={{ background: `${survey.color}cc` }}
              >
                <div className="text-white text-center">
                  <QrCode size={16} className="mx-auto mb-0.5" />
                  <span className="text-[9px] font-bold">ABRIR</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <div className="text-center">
        <p className="text-[11px] font-semibold" style={{ color: survey.color }}>{survey.label}</p>
        <p className="text-[9px]" style={{ color: 'var(--vz-text-muted)' }}>{survey.desc}</p>
      </div>
    </motion.div>
  );
}

export default function Login() {
  const { isAuthenticated, loginWithCredentials } = useUserStore();
  const { theme } = useThemeStore();
  const logoSrc = theme === 'dark' ? LOGO_NEGATIVO : LOGO_POSITIVO;
  const [activeSurvey, setActiveSurvey] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Access request state (shown when MSAL user is not in RLS)
  const [showAccessRequest, setShowAccessRequest] = useState(false);
  const [showPendingSedes, setShowPendingSedes] = useState(false);
  const [accessEmail, setAccessEmail] = useState('');
  const [accessName, setAccessName] = useState('');
  const [accessMessage, setAccessMessage] = useState('');
  const [accessSubmitted, setAccessSubmitted] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);

  // Dynamic sedes for the access request form
  const [availableSedes, setAvailableSedes] = useState([]);
  const [selectedSedes, setSelectedSedes] = useState([]);
  const [sedeSearch, setSedeSearch] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Check for MSAL callbacks set by index.js pre-render handler
  useEffect(() => {
    const pending = sessionStorage.getItem('msal_pending');
    if (pending) {
      try {
        const { email: pEmail, name: pName } = JSON.parse(pending);
        sessionStorage.removeItem('msal_pending');
        setAccessEmail(pEmail || '');
        setAccessName(pName || pEmail?.split('@')[0] || '');
        setShowAccessRequest(true);
      } catch (_) {}
    }

    // Pending sedes: user is approved but sedes need to be re-assigned with correct codes
    const pendingSedes = sessionStorage.getItem('msal_pending_sedes');
    if (pendingSedes) {
      try {
        sessionStorage.removeItem('msal_pending_sedes');
        setShowPendingSedes(true);
      } catch (_) {}
    }

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('msal_error') === '1') {
      const reason = urlParams.get('reason') || '';
      if (reason.includes('900971') || reason.includes('redirect')) {
        setLoginError('El inicio de sesión de Microsoft requiere configuración en Azure. Contacta con el administrador.');
      } else {
        setLoginError('Error al iniciar sesión con tu cuenta de Valoriza. Inténtalo de nuevo.');
      }
      window.history.replaceState({}, document.title, '/login');
    }
  }, []);

  // Load available sedes from MongoDB cache (all sedes) when access request form opens
  useEffect(() => {
    if (!showAccessRequest || availableSedes.length > 0) return;
    fetch(`${BACKEND_URL}/api/filters/sedes-labels`)
      .then(r => r.json())
      .then(data => {
        // API returns [{value: label, label, code}]; use label as display string
        const strings = Array.isArray(data)
          ? data.map(item => (typeof item === 'object' && item.label ? item.label : String(item)))
          : [];
        setAvailableSedes(strings);
      })
      .catch(() => {});
  }, [showAccessRequest, availableSedes.length]);

  const filteredSedes = availableSedes
    .filter(s => s.toLowerCase().includes(sedeSearch.toLowerCase()) && !selectedSedes.includes(s));

  const toggleSede = (s) => {
    setSelectedSedes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  // Microsoft redirect-based login (same pattern as InnovationOS)
  const handleMsalLogin = async () => {
    setIsLoading(true);
    setLoginError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/microsoft/config`);
      if (!res.ok) throw new Error('Config no disponible');
      const config = await res.json();
      if (!config.enabled) throw new Error('Microsoft login no disponible');

      const state = btoa(JSON.stringify({ returnUrl: window.location.origin, ts: Date.now() }));
      const nonce = Math.random().toString(36).substring(2) + Date.now().toString(36);
      sessionStorage.setItem('msal_auth_state', state);
      sessionStorage.setItem('msal_auth_nonce', nonce);

      const redirectUri = window.location.origin;
      const params = new URLSearchParams({
        client_id: config.client_id,
        response_type: 'id_token token',
        redirect_uri: redirectUri,
        scope: 'openid profile email',
        response_mode: 'fragment',
        state,
        nonce,
        prompt: 'select_account',
      });

      window.location.href = `${config.authority}/oauth2/v2.0/authorize?${params.toString()}`;
    } catch (err) {
      setLoginError('Error al iniciar sesión. Inténtalo de nuevo.');
      setIsLoading(false);
    }
  };

  const handleAccessRequest = async (e) => {
    e.preventDefault();
    setAccessLoading(true);
    try {
      await apiAccessRequest({
        email: accessEmail,
        name: accessName,
        requested_sedes: selectedSedes,
        message: accessMessage || 'El usuario solicita acceso al dashboard.',
      });
      setAccessSubmitted(true);
    } catch {
      setLoginError('Error al enviar la solicitud. Inténtalo más tarde.');
    } finally {
      setAccessLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setLoginError('Introduce tu correo y contraseña.'); return; }
    setIsLoading(true);
    setLoginError('');
    try {
      const user = await apiLogin(email.trim(), password);
      loginWithCredentials(user);
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (detail === 'use_microsoft') {
        // User is in RLS (approved via Microsoft flow) — guide them to the right button
        setLoginError('');
        // Trigger the Microsoft login automatically for them
        handleMsalLogin();
      } else {
        setLoginError('Correo o contraseña incorrectos.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Pending sedes overlay – user is approved but codes need re-assignment
  if (showPendingSedes) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'var(--vz-bg-primary)' }}
        data-testid="pending-sedes-page"
      >
        <div
          className="w-full max-w-md rounded-2xl p-8 text-center"
          style={{ background: 'var(--vz-bg-card)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)' }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <h2 className="text-xl font-bold mb-3" style={{ color: '#fff' }}>
            Acceso pendiente de activación
          </h2>
          <p style={{ color: '#94a3b8', lineHeight: 1.7 }} className="mb-6 text-sm">
            Tu solicitud de acceso ha sido recibida, pero las sedes solicitadas aún no han sido activadas por los responsables. En breve recibirás un email de confirmación.
          </p>
          <div
            className="rounded-xl p-4 text-sm mb-6"
            style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)' }}
          >
            <p style={{ color: '#fdba74' }} className="font-medium mb-1">Para más información, contacta con:</p>
            <p style={{ color: '#94a3b8' }}>
              <strong style={{ color: '#e2e8f0' }}>Luis Olivares</strong> — <a href="mailto:lolivares@valorizasm.com" style={{ color: '#f97316' }}>lolivares@valorizasm.com</a>
            </p>
            <p style={{ color: '#94a3b8' }}>
              <strong style={{ color: '#e2e8f0' }}>Ana Belén Garzón</strong> — <a href="mailto:abgarzon@valorizasm.com" style={{ color: '#f97316' }}>abgarzon@valorizasm.com</a>
            </p>
          </div>
          <button
            onClick={() => setShowPendingSedes(false)}
            className="text-sm"
            style={{ color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Volver al inicio de sesión
          </button>
        </div>
      </div>
    );
  }

  // Access request overlay
  if (showAccessRequest) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'var(--vz-bg-primary)' }}
        data-testid="access-request-page"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md rounded-2xl border p-8"
          style={{
            background: 'var(--vz-bg-card)',
            borderColor: 'rgba(0,200,83,0.2)',
            boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
          }}
        >
          {accessSubmitted ? (
            <div className="text-center" data-testid="access-request-success">
              <CheckCircle size={48} className="mx-auto mb-4" style={{ color: '#00C853' }} />
              <h2 className="text-xl font-bold text-white mb-2">Solicitud enviada</h2>
              <p className="text-sm mb-6" style={{ color: 'var(--vz-text-secondary)' }}>
                Tu solicitud ha sido enviada a los administradores. Recibirás acceso una vez sea aprobada.
              </p>
              <Button
                onClick={() => { setShowAccessRequest(false); setAccessSubmitted(false); }}
                className="w-full"
                style={{ background: '#00C853', color: '#000' }}
              >
                Volver al inicio
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-6">
                <AlertCircle size={24} style={{ color: '#FFB900' }} />
                <div>
                  <h2 className="text-lg font-bold text-white">Acceso pendiente</h2>
                  <p className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>
                    Tu cuenta no tiene acceso configurado aún
                  </p>
                </div>
              </div>
              <p className="text-sm mb-5" style={{ color: 'var(--vz-text-secondary)' }}>
                Hola <strong className="text-white">{accessName}</strong>, tu cuenta{' '}
                <span style={{ color: '#00C853' }}>{accessEmail}</span> no tiene sedes asignadas.
                Envía una solicitud para que los administradores te configuren el acceso.
              </p>
              <form onSubmit={handleAccessRequest} className="space-y-4" data-testid="access-request-form">
                {/* Sedes selector — dynamic from MongoDB cache */}
                <div>
                  <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--vz-text-secondary)' }}>
                    Selecciona las sedes a las que necesitas acceder ({selectedSedes.length} seleccionadas
                    {availableSedes.length > 0 ? ` de ${availableSedes.length} disponibles` : ''})
                  </label>

                  {selectedSedes.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2 p-2 rounded-lg" style={{ background: 'rgba(0,200,83,0.06)', border: '1px solid rgba(0,200,83,0.2)' }}>
                      {selectedSedes.map(s => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => toggleSede(s)}
                          className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded"
                          style={{ background: 'rgba(0,200,83,0.15)', color: '#00C853', border: '1px solid rgba(0,200,83,0.3)' }}
                        >
                          {s.length > 22 ? s.substring(0, 20) + '…' : s} <span style={{ fontSize: 9 }}>✕</span>
                        </button>
                      ))}
                    </div>
                  )}

                  <input
                    type="text"
                    value={sedeSearch}
                    onChange={e => setSedeSearch(e.target.value)}
                    placeholder={`Buscar entre ${availableSedes.length || '...'} sedes...`}
                    className="w-full rounded-xl px-3 py-2 text-sm text-white outline-none mb-2"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--vz-border-subtle)' }}
                    data-testid="access-request-sede-search"
                  />

                  <div className="max-h-64 overflow-y-auto rounded-xl" style={{ border: '1px solid var(--vz-border-subtle)' }}>
                    {availableSedes.length === 0 ? (
                      <p className="text-xs text-center py-3" style={{ color: 'var(--vz-text-muted)' }}>Cargando sedes...</p>
                    ) : filteredSedes.length === 0 ? (
                      <p className="text-xs text-center py-3" style={{ color: 'var(--vz-text-muted)' }}>No hay resultados para "{sedeSearch}"</p>
                    ) : (
                      filteredSedes.map(s => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => toggleSede(s)}
                          className="w-full text-left text-xs px-3 py-1.5 transition-colors"
                          style={{ color: 'var(--vz-text-secondary)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                          data-testid={`sede-option-${s}`}
                        >
                          {s}
                        </button>
                      ))
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--vz-text-secondary)' }}>
                    Comentario adicional (opcional)
                  </label>
                  <textarea
                    value={accessMessage}
                    onChange={(e) => setAccessMessage(e.target.value)}
                    placeholder="Ej: Soy responsable de las instalaciones de Madrid Norte..."
                    rows={2}
                    className="w-full rounded-xl p-3 text-sm text-white resize-none outline-none"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--vz-border-subtle)' }}
                    data-testid="access-request-message"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 gap-2 font-semibold"
                  style={{ background: '#00C853', color: '#000' }}
                  disabled={accessLoading || selectedSedes.length === 0}
                  data-testid="access-request-submit"
                >
                  <Send size={15} />
                  {accessLoading ? 'Enviando...' : `Solicitar acceso (${selectedSedes.length} sedes)`}
                </Button>
                <button
                  type="button"
                  onClick={() => setShowAccessRequest(false)}
                  className="w-full text-xs py-2"
                  style={{ color: 'var(--vz-text-muted)' }}
                >
                  Cancelar y volver
                </button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <div
        className="min-h-screen flex items-center justify-center relative overflow-hidden px-4"
        style={{ background: 'var(--vz-bg-primary)' }}
        data-testid="login-page"
      >
        {/* Background */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1600&q=20')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.04,
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(800px circle at 20% 30%, rgba(0,200,83,0.05), transparent 60%), radial-gradient(900px circle at 80% 70%, rgba(74,158,255,0.05), transparent 60%)',
          }}
        />

        <div className="relative z-10 w-full flex flex-col lg:flex-row items-center justify-center gap-8 max-w-5xl mx-auto py-8">

          {/* Left: Surveys QR panel */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="w-full max-w-xs lg:max-w-[340px]"
          >
            <div
              className="rounded-2xl border p-5"
              style={{
                background: 'var(--vz-bg-card)',
                borderColor: 'rgba(255,255,255,0.08)',
                boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
              }}
            >
              {/* Logo */}
              <div className="flex items-center gap-3 mb-5">
                <img
                  src={logoSrc}
                  alt="Valoriza"
                  className="h-8 object-contain"
                  onError={(e) => { e.target.style.display='none'; }}
                />
                <div>
                  <p className="font-montserrat font-bold text-sm text-white">Valoriza</p>
                  <p className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>Sistema de Gestión OPS</p>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <QrCode size={14} style={{ color: 'var(--vz-text-secondary)' }} />
                  <p className="font-montserrat font-semibold text-sm text-white">Registra una actividad</p>
                </div>
                <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>
                  Escanea el código QR o toca para abrir el formulario directamente desde aquí
                </p>
              </div>

              {/* QR Cards */}
              <div className="flex justify-between gap-2">
                {SURVEYS.map((s) => (
                  <QRCard key={s.type} survey={s} onOpen={setActiveSurvey} />
                ))}
              </div>

              <p className="text-center text-[10px] mt-4" style={{ color: 'rgba(170,204,255,0.3)' }}>
                Powered by ArcGIS Survey123
              </p>
            </div>
          </motion.div>

          {/* Right: Login form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-full max-w-sm"
          >
            <div
              className="rounded-2xl border p-8"
              style={{
                background: 'var(--vz-bg-card)',
                borderColor: 'rgba(0,200,83,0.2)',
                boxShadow: '0 0 0 1px rgba(0,200,83,0.08), 0 25px 60px rgba(0,0,0,0.5)',
              }}
            >
              {/* Title */}
              <div className="flex flex-col items-center mb-7">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 overflow-hidden"
                  style={{ background: 'rgba(0,200,83,0.1)', border: '1px solid rgba(0,200,83,0.2)' }}
                >
                  <img
                    src={logoSrc}
                    alt="Valoriza"
                    className="w-12 h-12 object-contain p-1"
                    onError={(e) => { e.target.style.display='none'; }}
                  />
                </div>
                <h1 className="font-montserrat font-bold text-2xl text-white">Acceso</h1>
                <p className="font-figtree text-sm mt-1" style={{ color: 'var(--vz-text-secondary)' }}>
                  Dashboard Liderazgo / OPS 2026
                </p>
              </div>

              {/* MSAL Login */}
              <Button
                onClick={handleMsalLogin}
                className="w-full h-11 gap-3 mb-5 font-montserrat text-sm font-semibold"
                style={{ background: '#2F2F2F', border: '1px solid #444', color: '#fff' }}
                data-testid="login-msal-button"
              >
                <svg width="16" height="16" viewBox="0 0 21 21" fill="none">
                  <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                  <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                </svg>
                Inicia sesión con tu cuenta de Valoriza
              </Button>

              {/* Collapsible email login */}
              <button
                type="button"
                onClick={() => setShowEmailForm(v => !v)}
                className="w-full flex items-center justify-center gap-2 mt-2 py-2 text-xs rounded-xl transition-colors"
                style={{ color: showEmailForm ? 'var(--vz-text-secondary)' : 'var(--vz-text-muted)', background: 'transparent', border: 'none' }}
                data-testid="toggle-email-login"
              >
                <ChevronDown
                  size={13}
                  style={{ transform: showEmailForm ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.25s' }}
                />
                Acceso con contraseña
              </button>

              <AnimatePresence>
                {showEmailForm && (
                  <motion.div
                    key="email-form"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25 }}
                    style={{ overflow: 'hidden' }}
                  >
                    <div
                      className="rounded-xl p-4 mt-2 space-y-3"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--vz-border-subtle)' }}
                    >
                      {/* Email / Password Form */}
                      <form onSubmit={handleLogin} className="space-y-3" data-testid="login-form">
                        {/* Email */}
                        <div className="relative">
                          <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--vz-text-muted)' }} />
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => { setEmail(e.target.value); setLoginError(''); }}
                            placeholder="Correo electrónico"
                            className="w-full h-10 pl-9 pr-3 rounded-xl text-sm text-white outline-none transition-colors duration-200"
                            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--vz-border-subtle)' }}
                            data-testid="login-email-input"
                            autoComplete="username"
                          />
                        </div>

                        {/* Password */}
                        <div className="relative">
                          <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--vz-text-muted)' }} />
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => { setPassword(e.target.value); setLoginError(''); }}
                            placeholder="Contraseña"
                            className="w-full h-10 pl-9 pr-10 rounded-xl text-sm text-white outline-none transition-colors duration-200"
                            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--vz-border-subtle)' }}
                            data-testid="login-password-input"
                            autoComplete="current-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(v => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2"
                            style={{ color: 'var(--vz-text-muted)' }}
                            tabIndex={-1}
                          >
                            {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </div>

                        {/* Error */}
                        <AnimatePresence>
                          {loginError && (
                            <motion.p
                              initial={{ opacity: 0, y: -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              className="text-xs text-center px-2 py-1.5 rounded-lg"
                              style={{ color: '#FF5252', background: 'rgba(255,82,82,0.08)', border: '1px solid rgba(255,82,82,0.2)' }}
                              data-testid="login-error"
                            >
                              {loginError}
                            </motion.p>
                          )}
                        </AnimatePresence>

                        {/* Submit */}
                        <Button
                          type="submit"
                          disabled={isLoading}
                          className="w-full h-10 gap-2 font-montserrat text-sm font-semibold"
                          style={{ background: 'var(--vz-green)', color: '#fff' }}
                          data-testid="login-submit-btn"
                        >
                          {isLoading ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <><LogIn size={14} />Iniciar sesión</>
                          )}
                        </Button>
                      </form>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="text-center mt-6 space-y-1">
                <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>
                  Valoriza © 2026 — Sistema de gestión OPS
                </p>
                <p className="text-[10px] font-montserrat">
                  <span style={{ color: 'var(--vz-text-muted)' }}>Powered by </span>
                  <span className="font-semibold" style={{ color: 'var(--vz-green)' }}>Valoriza's Innovation Team</span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Survey Modal */}
      {activeSurvey && (
        <SurveyModal
          type={activeSurvey.type}
          url={activeSurvey.url}
          onClose={() => setActiveSurvey(null)}
        />
      )}
    </>
  );
}
